import { CustomSlideIndex } from '@llx/common';

import {
  HostFieldsFieldsFragment,
  HostFieldsFieldsFragmentDoc,
  MeetingFieldsFragment,
  MeetingFieldsFragmentDoc,
  UpdateHostFieldsInputDto,
  UpdateMeetingInputDto,
  useResetMeetingMutation,
  useUpdateHostFieldsMutation,
  useUpdateMeetingMutation,
} from '@/@generated/codegen/API';
import { useApolloClientContext } from '@/components/CustomApolloProvider/CustomApolloProvider';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { getMeetingStatus, MEETING_STATUS } from '../helpers/getMeetingStatus';

export default function useMeetingInteractionFunctions(
  meeting: MeetingFieldsFragment | null | undefined,
  hostFields: HostFieldsFieldsFragment | null | undefined,
  isPreview: boolean
) {
  const { apolloClient } = useApolloClientContext();
  const [mutationUpdateMeeting] = useUpdateMeetingMutation();
  const [mutationUpdateHostFields] = useUpdateHostFieldsMutation();
  const [mutationResetMeeting] = useResetMeetingMutation();
  const meetingStatus = meeting ? getMeetingStatus(meeting) : null;

  const updateCurrentCase = async (meetingID: string | undefined, patientCaseId: string | null) => {
    if (isPreview) {
      if (!meeting || !hostFields) {
        captureSentry({
          title: 'error at updateCurrentCase (preview): meeting or hostFields is undefined',
          detail: {
            meeting,
            meetingID,
            caseID: patientCaseId,
            hostFields,
          },
        });
        console.error('error at updateCurrentCase (preview): meeting is undefined');
        return;
      }
      apolloClient.cache.writeFragment({
        id: apolloClient.cache.identify(hostFields),
        fragment: HostFieldsFieldsFragmentDoc,
        data: {
          currentCase: patientCaseId,
        } satisfies Partial<HostFieldsFieldsFragment>,
      });
      return;
    }
    if (!meetingID) {
      captureSentry({
        title: `error at updateCurrentCase: meetingID is undefined`,
        detail: {
          meeting,
          caseID: patientCaseId,
          meetingID,
        },
      });
      console.error(`error at updateCurrentCase: meetingID is undefined`);
      return;
    }

    try {
      await mutationUpdateHostFields({
        variables: {
          data: {
            meetingId: meetingID,
            currentCase: {
              set: patientCaseId ?? null,
            },
            currentSlide: {
              set: 0,
            },
            currentAssetID: {
              set: null,
            },
          },
        },
      });
    } catch (error) {
      captureSentry({
        title: `error at updateCurrentCase:`,
        error,
        detail: {
          meeting,
          caseID: patientCaseId,
          meetingID,
        },
      });
      console.error(`error at updateCurrentCase:`);
      console.error(error);
    }
  };

  const updateCurrentSlide = async (meetingID: string | undefined, slideIndex: number) => {
    if (isPreview) {
      if (!meeting || !hostFields) {
        captureSentry({
          title: 'error at updateCurrentSlide (preview): meeting or hostfields is undefined',
          detail: {
            meeting,
            meetingID,
            slideIndex,
            hostFields,
          },
        });
        console.error('error at updateCurrentSlide (preview): meeting is undefined');
        return;
      }
      apolloClient.cache.writeFragment({
        id: apolloClient.cache.identify(hostFields),
        fragment: HostFieldsFieldsFragmentDoc,
        data: {
          currentSlide: slideIndex,
        } satisfies Partial<HostFieldsFieldsFragment>,
      });
      return;
    }
    if (!meetingID) {
      captureSentry({
        title: `error at updateCurrentSlide: meetingID is undefined`,
        detail: {
          meeting,
          meetingID,
          slideIndex,
        },
      });
      console.error(`error at updateCurrentSlide: meetingID is undefined`);
      return;
    }
    try {
      await mutationUpdateHostFields({
        variables: {
          data: {
            meetingId: meetingID,
            currentSlide: {
              set: slideIndex,
            },
          },
        },
      });
    } catch (error) {
      captureSentry({
        title: `error at updateCurrentSlide:`,
        error,
        detail: {
          meeting,
          meetingID,
          slideIndex,
        },
      });
      console.error(`error at updateCurrentSlide:`);
      console.error(error);
    }
  };

  const restartMeeting = async (meetingID: string) => {
    try {
      if (isPreview) {
        if (!meeting || !hostFields) {
          captureSentry({
            title: 'error at restartMeeting (preview): meeting or hostFields is undefined',
            detail: {
              meeting,
              meetingID,
              hostFields,
            },
          });
          console.error('error at restartMeeting (preview): meeting is undefined');
          return;
        }
        apolloClient.cache.writeFragment({
          id: apolloClient.cache.identify(hostFields),
          fragment: HostFieldsFieldsFragmentDoc,
          data: {
            currentSlide: 0,
            currentCase: null,
          } satisfies Partial<HostFieldsFieldsFragment>,
        });
        return;
      }

      await mutationResetMeeting({
        variables: {
          where: {
            id: meetingID,
          },
          data: {
            setStartDate: meetingStatus ? meetingStatus !== MEETING_STATUS.TESTING : false,
          },
        },
      });
    } catch (error) {
      captureSentry({
        title: `error at restartMeeting:`,
        error,
        detail: {
          meeting,
          meetingID,
        },
      });
      console.error(`error at restartMeeting:`);
      console.error(error);
    }
  };

  const showNPSScoreView = async (meeting: MeetingFieldsFragment) => {
    const updateHostFieldsDto: UpdateHostFieldsInputDto = {
      meetingId: meeting.id,
      currentCase: {
        set: null,
      },
      currentSlide: {
        set: CustomSlideIndex.NPS_SCORE,
      },
    };
    const updateMeetingDto: UpdateMeetingInputDto = {
      id: meeting.id,
    };
    if (!meeting.isDemo && !meeting.isInTesting) {
      updateMeetingDto.endDate = new Date(new Date().getTime() + 5 * 60000);
    }
    try {
      if (isPreview) {
        if (!meeting || !hostFields) {
          captureSentry({
            title: 'error at showNPSScoreView (preview): meeting or hostFields is undefined',
            detail: {
              meeting,
            },
          });
          console.error('error at showNPSScoreView (preview): meeting is undefined');
          return;
        }

        if (updateMeetingDto.endDate) {
          apolloClient.cache.writeQuery({
            id: apolloClient.cache.identify(meeting),
            query: MeetingFieldsFragmentDoc,
            data: {
              endDate: updateMeetingDto.endDate,
            } satisfies Partial<MeetingFieldsFragment>,
          });
        }
        apolloClient.cache.writeFragment({
          id: apolloClient.cache.identify(hostFields),
          fragment: HostFieldsFieldsFragmentDoc,
          data: {
            currentCase: null,
            currentSlide: CustomSlideIndex.NPS_SCORE,
          } satisfies Partial<HostFieldsFieldsFragment>,
        });
        return;
      }
      await mutationUpdateHostFields({
        variables: {
          data: updateHostFieldsDto,
        },
      });
      if (Object.keys(updateMeetingDto).length > 0) {
        await mutationUpdateMeeting({
          variables: {
            data: updateMeetingDto,
          },
        });
      }
    } catch (error) {
      captureSentry({
        title: `error at showNPSScoreView:`,
        error,
        detail: {
          meeting,
        },
      });
      console.error(`error at showNPSScoreView:`);
      console.error(error);
    }
  };

  return {
    updateCurrentCase,
    updateCurrentSlide,
    restartMeeting,
    showNPSScoreView,
  };
}
